"use client";

import Link from "next/link";
import { useMemo, useRef, useState } from "react";
import { useInvoiceStore } from "@/lib/invoice/store";
import { idbSet } from "@/lib/invoice/idb";
import { parseInvoiceImage, parseInvoicePdf, parseSpreadsheetFile } from "@/lib/invoice/importers";
import { detectPlace } from "@/lib/invoice/geo";
import { aiConfig, aiReadInvoice } from "@/lib/invoice/ai";
import { suggestTag } from "@/lib/invoice/tags";
import { looksRecurring } from "@/lib/invoice/projectHealth";
import { CURRENCIES, computeTotals, counterKey, fmtDate, fmtMoney, formatNumber, invoiceStatus, monthLabel, num, projectLine, round2, uid } from "@/lib/invoice/utils";
import { autoTaxMode, placeOfSupply } from "@/components/Invoice/InvoiceEditor";
import { Button, Card, MonthTag, PageHeader, StatusBadge, cx, useFeedback } from "@/components/Invoice/ui";
import { ImportConfirm, ImportReading } from "@/components/Invoice/ImportConfirm";

const norm = (s) => String(s || "").toLowerCase().replace(/&/g, "and").replace(/\b(ltd|limited|llc|llp|inc|pvt|private|plc|gmbh|co|company|group|the|corp|corporation)\b\.?/g, "").replace(/[^a-z0-9]/g, "");
const cleanId = (s) => String(s || "").replace(/[\s-]/g, "").toUpperCase();

function matchClient(clients, billTo) {
  const gst = cleanId(billTo?.gstin);
  if (gst) {
    const c = clients.find((x) => cleanId(x.gstin) === gst);
    if (c) return c;
  }
  const email = String(billTo?.email || "").toLowerCase();
  if (email) {
    const c = clients.find((x) => String(x.email || "").toLowerCase() === email || String(x.ccEmail || "").toLowerCase() === email);
    if (c) return c;
  }
  const n = norm(billTo?.name);
  if (!n) return null;
  return clients.find((c) => norm(c.name) === n) || clients.find((c) => norm(c.name).length > 3 && (norm(c.name).includes(n) || n.includes(norm(c.name)))) || null;
}

function matchProject(projects, desc) {
  const n = norm(desc);
  if (!n) return null;
  return (
    projects.find((p) => norm(projectLine(p)) === n) ||
    projects.find((p) => norm(p.name) === n) ||
    projects.find((p) => norm(p.name) && n.startsWith(norm(p.name)) && (!p.service || n.includes(norm(p.service)))) ||
    projects.find((p) => norm(p.name).length > 3 && n.startsWith(norm(p.name))) ||
    null
  );
}

const ACCEPT = ".pdf,.csv,.tsv,.txt,.xlsx,.png,.jpg,.jpeg,.webp";

export default function ImportPage() {
  const store = useInvoiceStore();
  const { toast, confirm } = useFeedback();
  const [busy, setBusy] = useState("");
  const [last, setLast] = useState(null); // { batch, results, failed }
  const [open, setOpen] = useState(null);
  const [drag, setDrag] = useState(false);
  const [confirming, setConfirming] = useState(null); // { parsed, failed } waiting for "Confirm invoice details"
  const [reading, setReading] = useState(null); // { preview, index, total, stage, detail } while files are read
  const cancelRead = useRef(false);
  const fileRef = useRef(null);

  // previous imports, straight from the saved data (so undo survives a reload)
  const history = useMemo(() => {
    const m = new Map();
    for (const inv of store.invoices) {
      const b = inv.imported?.batch;
      if (!b) continue;
      const e = m.get(b) || { batch: b, at: inv.imported.at, count: 0, files: new Set() };
      e.count++;
      e.files.add(inv.imported.from);
      m.set(b, e);
    }
    return [...m.values()].sort((a, b) => String(b.at).localeCompare(String(a.at))).slice(0, 8);
  }, [store.invoices]);

  if (!store.ready) return null;

  const brand = store.defaultBrand || store.brandById(store.settings.defaultBrandId);
  const indianBrand = !brand?.country || /^india$/i.test(brand.country);

  // When the built-in reader isn't sure (no lines, no number, totals don't add up),
  // ask Gemini for a second reading – only if AI is switched on in Settings.
  async function aiAssist(p) {
    const ai = aiConfig(store.settings);
    const lines = (p.items || []).reduce((t, l) => t + num(l.amount), 0);
    const tax = num(p.cgst) + num(p.sgst) + num(p.igst) + num(p.vat);
    const unsure = !p.items?.length || !p.number || (p.total && Math.abs(lines + tax - num(p.discount) - num(p.credit) - p.total) > 0.05) || p.ocr;
    if (!ai.enabled || !ai.importHelp || !unsure || !p.text) return p;
    try {
      report(`Asking AI to double-check ${p.source}…`);
      const a = await aiReadInvoice(store.settings, p.text);
      const aSum = a.items.reduce((t, l) => t + l.amount, 0) + a.vat - a.discount - a.credit;
      const aGood = a.items.length && (!a.total || Math.abs(aSum - a.total) < 0.05);
      if (!aGood) return p;
      return {
        ...p,
        number: p.number || a.number,
        date: p.date || a.date,
        dueDate: p.dueDate || a.dueDate,
        currency: p.currency || a.currency,
        billTo: p.billTo?.name ? p.billTo : { ...a.billTo, ...Object.fromEntries(Object.entries(p.billTo || {}).filter(([, v]) => v)) },
        items: a.items,
        discount: a.discount,
        credit: a.credit,
        total: a.total || p.total,
        cgst: 0,
        sgst: 0,
        igst: 0,
        vat: a.vat,
        taxMode: a.vat ? p.taxMode || (a.currency === "INR" ? "intra" : "vat") : p.taxMode,
        taxLabel: p.taxLabel || (a.vat ? "Tax" : ""),
        status: p.status || a.status,
        warnings: [...(p.warnings || []).filter((w) => !/Lines add up|No line items|weren't listed/.test(w)), "Read with AI (Gemini) – please double-check"],
      };
    } catch (e) {
      return { ...p, warnings: [...(p.warnings || []), `AI check skipped: ${e.message}`] };
    }
  }

  // progress text goes to the page spinner and to the "reading" window
  function report(msg) {
    setBusy(msg);
    setReading((r) => (r ? { ...r, detail: msg } : r));
  }

  async function readFile(file, progress = report) {
    const name = file.name;
    if (/\.pdf$/i.test(name)) return [await aiAssist({ ...(await parseInvoicePdf(await file.arrayBuffer(), undefined, { fileName: name, onProgress: progress })), source: name, file })];
    if (/\.(png|jpe?g|webp)$/i.test(name)) return [await aiAssist({ ...(await parseInvoiceImage(file, { onProgress: progress })), source: name, file })];
    if (/\.(csv|tsv|txt|xlsx|xls)$/i.test(name)) {
      const order = store.settings.defaults.currency === "USD" ? "mdy" : "dmy";
      return (await parseSpreadsheetFile(file, { order })).map((d) => ({ ...d, source: d.source || name, file: null }));
    }
    throw new Error("Unsupported file type");
  }

  async function handleFiles(fileList) {
    const files = [...fileList].filter((f) => new RegExp(`(${ACCEPT.replace(/\./g, "\\.").replace(/,/g, "|")})$`, "i").test(f.name));
    if (!files.length) return toast("Choose PDF, Excel (.xlsx), CSV or image files.", { tone: "error" });
    const parsed = [];
    const failed = [];
    const pause = (ms) => new Promise((r) => setTimeout(r, ms));
    cancelRead.current = false;
    for (let i = 0; i < files.length; i++) {
      if (cancelRead.current) break;
      setBusy(`Reading ${files[i].name} (${i + 1}/${files.length})…`);
      setReading({ preview: { file: files[i], source: files[i].name, items: [] }, index: i, total: files.length, stage: "upload", detail: "" });
      await pause(350);
      setReading((r) => ({ ...r, stage: "extract", detail: "" }));
      try {
        const got = await readFile(files[i]);
        setReading((r) => ({ ...r, stage: "match", detail: got[0]?.billTo?.name ? `Found ${got[0].billTo.name}` : "" }));
        await pause(450);
        if (!got.length) failed.push({ fileName: files[i].name, error: "No invoices found in this file." });
        for (const p of got) {
          if (!p.items?.length && !p.total && !p.number) failed.push({ fileName: p.source, error: p.warnings?.[0] || "Nothing that looks like an invoice was found." });
          else parsed.push(p);
        }
      } catch (e) {
        failed.push({ fileName: files[i].name, error: e?.message || String(e) });
      }
    }
    setBusy("");
    setReading(null);
    if (cancelRead.current) return;
    // show what was read, one invoice at a time, before anything is saved
    if (parsed.length) setConfirming({ parsed, failed });
    else setLast({ batch: null, results: [], failed });
  }

  async function confirmDone(chosen, skipped) {
    const failed = [...(confirming?.failed || []), ...skipped.map((fileName) => ({ fileName, error: "Skipped – not imported" }))];
    setConfirming(null);
    if (!chosen.length) return setLast({ batch: null, results: [], failed });
    setBusy("Matching clients and projects…");
    try {
      await importParsed(chosen, failed);
    } finally {
      setBusy("");
    }
  }

  async function importParsed(parsed, failed) {
    const batch = uid();
    const at = new Date().toISOString();
    const clients = [...store.clients];
    const projects = [...store.projects];
    const newClients = [];
    const newProjects = [];
    const invoices = [];
    const results = [];
    const counters = {};
    const taken = new Set(store.invoices.map((i) => `${i.number}|${i.clientId}`));
    const takenNumbers = new Set(store.invoices.map((i) => i.number).filter(Boolean));

    // lines that repeat across months are retainers → active monthly projects
    const seenMonths = new Map();
    for (const p of parsed) for (const l of p.items || []) {
      const k = `${norm(p.billTo?.name)}|${norm(l.description)}`;
      if (!seenMonths.has(k)) seenMonths.set(k, new Set());
      seenMonths.get(k).add((p.date || "").slice(0, 7));
    }

    for (const p of parsed) {
      // ---- client ----
      // The client picked on the confirm screen wins. "New client" means create it –
      // unless an earlier invoice in this batch already created one with that name.
      let client = p.clientId
        ? clients.find((c) => c.id === p.clientId) || null
        : p.confirmed
          ? clients.find((c) => norm(c.name) === norm(p.billTo?.name)) || null
          : matchClient(clients, p.billTo);
      let isNewClient = false;
      if (!client && p.billTo?.name) {
        const place = await detectPlace(p.billTo);
        client = {
          id: uid(),
          name: p.billTo.name.trim(),
          contactPerson: p.billTo.contactPerson || "",
          email: p.billTo.email || "",
          phone: p.billTo.phone || "",
          address: place.country ? place.address : p.billTo.address || "",
          area: "",
          city: place.city || p.billTo.city || "",
          state: place.state || p.billTo.state || "",
          postcode: place.postcode || "",
          country: p.countryConfirmed || place.country || p.billTo.country || "",
          gstin: p.billTo.gstin || "",
          currency: CURRENCIES[p.currency] ? p.currency : "",
          taxMode: "",
          notes: `Created from imported file ${p.source}`,
          importBatch: batch,
        };
        clients.push(client);
        newClients.push(client);
        isNewClient = true;
      }
      const number = String(p.number || p.guessNumber || "").trim();
      if (number && client && taken.has(`${number}|${client.id}`)) {
        results.push({ skipped: true, fileName: p.source, number, clientName: client.name, reason: "Already imported" });
        continue;
      }
      if (number && !client && takenNumbers.has(number)) {
        results.push({ skipped: true, fileName: p.source, number, clientName: "", reason: "Already imported" });
        continue;
      }
      taken.add(`${number}|${client?.id || ""}`);
      takenNumbers.add(number);

      // ---- lines → projects ----
      const currency = CURRENCIES[p.currency] ? p.currency : client?.currency || store.settings.defaults.currency;
      const clientProjects = client ? projects.filter((x) => x.clientId === client.id) : [];
      const items = (p.items || []).map((l) => {
        let proj = client ? matchProject(clientProjects, l.description) : null;
        if (!proj && client && l.description) {
          // billed in 2+ months, or clearly ongoing work (SEO keywords, ads, maintenance…)
          const recurring = (seenMonths.get(`${norm(p.billTo?.name)}|${norm(l.description)}`)?.size || 0) > 1 || looksRecurring(l.description);
          const month = (p.periodMonth || p.date || "").slice(0, 7);
          proj = {
            id: uid(),
            clientId: client.id,
            name: l.description,
            service: "",
            billing: recurring ? "monthly" : "one-time",
            rate: num(l.rate),
            qty: num(l.qty) || 1,
            taxRate: num(l.taxRate),
            hsn: l.hsn || "",
            status: recurring ? "active" : "completed",
            startDate: recurring && month ? `${month}-01` : "",
            tag: suggestTag(l.description, store.settings.tags),
            notes: `From imported invoice ${number || p.source}`,
            importBatch: batch,
          };
          projects.push(proj);
          clientProjects.push(proj);
          newProjects.push(proj);
        }
        return { id: uid(), projectId: proj?.id || "", description: l.description || "", hsn: l.hsn || "", qty: num(l.qty) || 1, rate: num(l.rate), taxRate: num(l.taxRate), tag: proj?.tag || suggestTag(l.description, store.settings.tags) || "" };
      });

      // ---- invoice ----
      const brandId = client?.brandId || store.settings.defaultBrandId;
      const invBrand = store.brandById(brandId);
      const taxMode = p.taxMode || (client && !isNewClient ? autoTaxMode(client, store.settings, invBrand) : indianBrand && currency !== "INR" ? "export" : "none");
      const b = p.billTo || {};
      const id = uid();
      const date = p.date || "";
      const inv = {
        id,
        docType: p.docType || "invoice",
        number,
        status: p.status === "cancelled" ? "cancelled" : p.status === "draft" ? "draft" : "sent",
        brandId,
        template: "",
        date,
        dueDate: p.dueDate || "",
        currency,
        taxMode,
        taxLabel: p.taxLabel || "",
        clientId: client?.id || "",
        billTo: client
          ? { name: client.name, contactPerson: client.contactPerson || "", email: client.email || "", phone: client.phone || "", address: client.address || "", area: client.area || "", city: client.city || "", state: client.state || "", postcode: client.postcode || "", country: client.country || "", gstin: client.gstin || "" }
          : { name: b.name || "", email: b.email || "", phone: b.phone || "", address: b.address || "", country: b.country || "", gstin: b.gstin || "" },
        placeOfSupply: client ? placeOfSupply(client) : "",
        periodMonth: date.slice(0, 7),
        period: p.period || monthLabel(date.slice(0, 7)),
        items,
        discount: num(p.discount),
        credit: num(p.credit),
        roundOff: num(p.roundOff),
        creditLabel: "Credit Amount",
        notes: p.notes || "",
        terms: "",
        copyLabel: "ORIGINAL FOR RECIPIENT",
        tags: [],
        payments: [],
        imported: { from: p.source, at, batch },
        history: [{ at, action: `Imported from ${p.source}` }],
      };
      let t = computeTotals(inv);
      const fileTotal = num(p.total);
      if (fileTotal && Math.abs(fileTotal - t.total) >= 0.005) {
        const diff = round2(fileTotal - t.total);
        const taxable = t.subtotal;
        const fileTax = round2(num(p.cgst) + num(p.sgst) + num(p.igst) + num(p.vat));
        if (Math.abs(diff) <= 1) {
          inv.roundOff = round2(num(inv.roundOff) + diff); // per-line rounding in the original tool
        } else if (fileTax && taxable && inv.items.length) {
          // the file's tax isn't a round percentage – use the exact rate it implies
          const exact = Math.round((fileTax / taxable) * 1e6) / 1e4;
          const trial = computeTotals({ ...inv, items: inv.items.map((l) => ({ ...l, taxRate: exact })) });
          if (Math.abs(trial.total - fileTotal) < Math.abs(diff)) {
            inv.items = inv.items.map((l) => ({ ...l, taxRate: exact }));
            const left = round2(fileTotal - trial.total);
            if (left && Math.abs(left) <= 1) inv.roundOff = round2(num(inv.roundOff) + left);
          }
        }
        t = computeTotals(inv);
      }
      const paidAll = !p.status || p.status === "paid";
      const paid = paidAll ? t.total : Math.min(num(p.paid), t.total);
      if (paid > 0 && inv.docType === "invoice" && inv.status !== "cancelled" && inv.status !== "draft") {
        inv.payments.push({ id: uid(), date: inv.dueDate || inv.date || at.slice(0, 10), amount: round2(paid), tds: 0, method: "Imported", ref: paidAll && !p.status ? "Past invoice – assumed paid" : "From the imported file" });
      }
      if (p.file) {
        try {
          await idbSet(`file:${id}`, p.file);
          inv.originalFile = { name: p.file.name, type: p.file.type || "application/pdf", size: p.file.size };
        } catch {
          /* keeping the original is optional */
        }
      }
      // keep auto-numbering ahead of imported numbers
      const seq = Number((number.match(/(\d+)\s*$/) || [])[1]);
      if (seq && date && formatNumber(store.settings, inv.docType, date, seq, invBrand) === number) {
        const k = counterKey(store.settings, inv.docType, date, invBrand);
        counters[k] = Math.max(counters[k] || 0, seq + 1);
      }
      invoices.push(inv);
      t = computeTotals(inv);
      results.push({
        invoiceId: id,
        fileName: p.source,
        number,
        clientName: client?.name || "",
        newClient: isNewClient,
        date,
        periodMonth: inv.periodMonth || "",
        currency,
        lines: items.length,
        linked: items.filter((l) => l.projectId).length,
        total: t.total,
        fileTotal,
        matches: !fileTotal || Math.abs(fileTotal - t.total) < 0.05,
        status: invoiceStatus(inv, t),
        assumedPaid: paidAll && !p.status,
        warnings: [...(p.warnings || []).filter((w) => !/^Lines add up/.test(w)), ...(!number ? ["No invoice number in the file – left blank"] : []), ...(!date ? ["No date in the file – left blank"] : []), ...(!client ? ["No client name in the file – left blank"] : [])],
        items: items.map((l) => ({ description: l.description, qty: l.qty, rate: l.rate, taxRate: l.taxRate })),
        taxMode,
      });
    }

    if (invoices.length) store.importBatch({ clients: newClients, projects: newProjects, invoices, counters });
    setLast({ batch: invoices.length ? batch : null, results, failed, newClients: newClients.length, newProjects: newProjects.length });
    if (invoices.length) toast(`Imported ${invoices.length} invoice${invoices.length > 1 ? "s" : ""}${newClients.length ? `, ${newClients.length} new client${newClients.length > 1 ? "s" : ""}` : ""}`, { tone: "success" });
    else if (results.length) toast("Those invoices were already imported", { tone: "error" });
  }

  // ids: one invoice (the row's Paid / Unpaid switch) or null for the whole batch
  const setBatchPaid = (batch, paid, ids = null) => {
    const list = store.invoices.filter((i) => i.imported?.batch === batch && (!ids || ids.includes(i.id)) && (i.docType || "invoice") === "invoice" && i.status !== "cancelled");
    const updated = list.map((inv) => {
      const payments = (inv.payments || []).filter((x) => x.method !== "Imported");
      const t = computeTotals({ ...inv, payments });
      if (paid && t.balance > 0) payments.push({ id: uid(), date: inv.dueDate || inv.date || new Date().toISOString().slice(0, 10), amount: t.balance, tds: 0, method: "Imported", ref: "Marked paid on import" });
      return { ...inv, payments };
    });
    store.updateInvoices(updated);
    setLast((l) => (l && l.batch === batch ? { ...l, results: l.results.map((r) => (r.invoiceId && (!ids || ids.includes(r.invoiceId)) ? { ...r, status: paid ? "paid" : "sent", assumedPaid: false } : r)) } : l));
    toast(ids ? `${list[0]?.number || "Invoice"} marked ${paid ? "paid" : "unpaid"}` : paid ? "All marked as paid" : "All marked as unpaid", { tone: "success" });
  };

  const undo = async (batch, count) => {
    if (!(await confirm(`Remove the ${count} invoice${count > 1 ? "s" : ""} from this import, plus any clients and projects it created?`, { okLabel: "Undo import", danger: true }))) return;
    store.undoImport(batch);
    if (last?.batch === batch) setLast(null);
    toast("Import undone", { tone: "success" });
  };

  const done = last?.results.filter((r) => r.invoiceId) || [];
  const skipped = last?.results.filter((r) => r.skipped) || [];

  return (
    <div>
      <PageHeader
        eyebrow="Tools"
        title="Import past invoices"
        subtitle="Drop your old invoices – PDFs, scans, photos, Excel or CSV. Each one is read automatically, then you confirm the client, number, amount, dates and paid status next to the original before it's imported."
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (!busy) handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !busy && fileRef.current?.click()}
        className={cx(
          "mb-6 cursor-pointer rounded-2xl border-2 border-dashed bg-white px-6 py-12 text-center shadow-xs transition",
          drag ? "border-brand-500 bg-brand-50" : "border-slate-300 hover:border-brand-400 hover:bg-slate-50",
          busy && "cursor-wait"
        )}
      >
        <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => handleFiles(e.target.files).finally(() => (e.target.value = ""))} />
        {busy ? (
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
            <div className="font-semibold text-slate-800">{busy}</div>
          </div>
        ) : (
          <>
            <div className="text-base font-semibold text-slate-900">Drop invoices here, or click to choose</div>
            <div className="mt-1 text-sm text-slate-500">PDF · scanned PDF · JPG/PNG photo · Excel (.xlsx) · CSV — many files at once is fine</div>
          </>
        )}
      </div>

      {reading && (
        <ImportReading
          reading={{
            ...reading,
            onCancel: () => {
              cancelRead.current = true;
              setReading(null);
            },
          }}
        />
      )}

      {confirming && (
        <ImportConfirm
          drafts={confirming.parsed}
          store={store}
          matchClient={matchClient}
          onReplace={async (file, progress) => {
            const got = await readFile(file, progress);
            return got.filter((p) => p.items?.length || p.total || p.number);
          }}
          onCancel={() => setConfirming(null)}
          onDone={confirmDone}
        />
      )}

      {last && (
        <Card
          title={done.length ? `Imported ${done.length} invoice${done.length > 1 ? "s" : ""}` : "Nothing imported"}
          subtitle={done.length ? `${last.newClients || 0} new client(s) · ${last.newProjects || 0} new project(s)${skipped.length ? ` · ${skipped.length} already imported, skipped` : ""}` : undefined}
          actions={
            last.batch && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => setBatchPaid(last.batch, false)}>Mark all unpaid</Button>
                <Button size="sm" variant="secondary" onClick={() => setBatchPaid(last.batch, true)}>Mark all paid</Button>
                <Button size="sm" variant="danger" onClick={() => undo(last.batch, done.length)}>Undo import</Button>
              </div>
            )
          }
          className="mb-6"
        >
          {done.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm" data-testid="import-results">
                <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="py-2">Invoice</th>
                    <th className="py-2">Client</th>
                    <th className="py-2">Date</th>
                    <th className="py-2">Lines</th>
                    <th className="py-2 text-right">Total</th>
                    <th className="py-2 pl-4">Status</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 align-top">
                  {done.map((r) => (
                    <FragmentRow key={r.invoiceId} r={r} open={open === r.invoiceId} onToggle={() => setOpen(open === r.invoiceId ? null : r.invoiceId)} dateStyle={store.settings.defaults.dateStyle} onPaid={(paid) => setBatchPaid(last.batch, paid, [r.invoiceId])} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {skipped.length > 0 && (
            <div className="mt-3 text-xs text-slate-500">
              Skipped (already in your invoices): {skipped.map((r) => `${r.number || r.fileName}`).join(", ")}
            </div>
          )}
          {last.failed?.length > 0 && (
            <div className="mt-4 space-y-1 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
              {last.failed.map((f) => (
                <div key={f.fileName + f.error}>
                  <b>{f.fileName}</b>: {f.error}
                </div>
              ))}
            </div>
          )}
          {done.some((r) => r.assumedPaid) && (
            <div className="mt-3 text-xs text-slate-500">Change any invoice’s payment with its Paid / Unpaid buttons, or use “Mark all unpaid”.</div>
          )}
        </Card>
      )}

      {!last && !busy && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {[
            ["Reads any layout", "Finds the item table by its column headings – Description, Qty, Rate, Tax, Amount – whatever they’re called and wherever they are. Works with other tools’ invoices too (Zoho, QuickBooks, Stripe, Tally, Excel templates…)."],
            ["Maps everything", "Clients are matched by tax ID, email or name, or created with their address split into country, state and city. Each line is linked to a project; lines that repeat every month become monthly retainers."],
            ["Nothing to fill in", "Numbers, dates, currency, GST/VAT, discounts, credits and paid status are taken from the file. Fields the file doesn’t have stay blank. Undo is one click."],
          ].map(([h, t]) => (
            <Card key={h} title={h}>
              <div className="text-sm leading-relaxed text-slate-600">{t}</div>
            </Card>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <Card title="Recent imports" className="mt-6">
          <div className="divide-y divide-slate-100 text-sm">
            {history.map((h) => (
              <div key={h.batch} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <b>{h.count} invoice{h.count > 1 ? "s" : ""}</b> <span className="text-slate-500">· {[...h.files].slice(0, 3).join(", ")}{h.files.size > 3 ? ` +${h.files.size - 3} more` : ""}</span>
                  <div className="text-[11px] text-slate-400">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => undo(h.batch, h.count)}>Undo</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="mt-6 text-xs text-slate-500">
        Tip: after importing, open <Link className="text-brand-700 underline" href="/invoice/reports">Reports</Link> to see revenue history by client, project and service.
      </div>
    </div>
  );
}

function FragmentRow({ r, open, onToggle, dateStyle, onPaid }) {
  const isPaid = r.status === "paid";
  const canPay = r.status !== "cancelled" && r.status !== "draft";
  return (
    <>
      <tr>
        <td className="py-2.5">
          <Link href={`/invoice/invoices/view?id=${r.invoiceId}`} className="font-semibold text-slate-900 hover:text-brand-700">{r.number || <span className="italic text-slate-400">no number</span>}</Link>
          <div className="max-w-[220px] truncate text-[11px] text-slate-500">{r.fileName}</div>
        </td>
        <td className="py-2.5">
          {r.clientName || <span className="italic text-slate-400">—</span>}
          {r.clientName && <div className={cx("text-[11px]", r.newClient ? "text-blue-700" : "text-emerald-700")}>{r.newClient ? "New client created" : "Matched existing client"}</div>}
        </td>
        <td className="py-2.5">
          {r.date ? (
            <>
              <MonthTag date={r.periodMonth || r.date} />
              <div className="mt-1 text-[11px] text-slate-500">{fmtDate(r.date, dateStyle)}</div>
            </>
          ) : (
            <span className="italic text-slate-400">—</span>
          )}
        </td>
        <td className="py-2.5">
          {r.lines}
          <div className="text-[11px] text-slate-500">{r.linked} linked to projects</div>
        </td>
        <td className="py-2.5 text-right">
          <b>{fmtMoney(r.total, r.currency)}</b>
          {r.fileTotal > 0 && <div className={cx("text-[11px]", r.matches ? "text-emerald-700" : "text-amber-700")}>{r.matches ? "✓ matches file" : `File says ${fmtMoney(r.fileTotal, r.currency)}`}</div>}
        </td>
        <td className="py-2.5 pl-4">
          <StatusBadge status={r.status} />
          {canPay && (
            <div className="mt-1.5 inline-flex overflow-hidden rounded-lg text-[11px] font-semibold ring-1 ring-slate-200" role="group" aria-label="Payment status" data-testid="row-paid">
              <button type="button" onClick={() => !isPaid && onPaid(true)} className={cx("px-2.5 py-1 transition", isPaid ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-emerald-50")}>
                Paid
              </button>
              <button type="button" onClick={() => isPaid && onPaid(false)} className={cx("px-2.5 py-1 transition", !isPaid ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-amber-50")}>
                Unpaid
              </button>
            </div>
          )}
          {r.warnings.map((w) => (
            <div key={w} className="mt-1 max-w-[240px] text-[11px] text-amber-700">{w}</div>
          ))}
        </td>
        <td className="py-2.5 text-right">
          <button className="text-xs font-medium text-slate-500 hover:text-slate-900" onClick={onToggle}>{open ? "Hide lines" : "Show lines"}</button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="bg-slate-50/60 px-3 pb-3">
            <table className="w-full text-xs">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1.5">Description</th>
                  <th className="py-1.5 text-right">Qty</th>
                  <th className="py-1.5 text-right">Rate</th>
                  <th className="py-1.5 text-right">Tax %</th>
                </tr>
              </thead>
              <tbody>
                {r.items.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1">{l.description || <span className="italic text-slate-400">(no description in file)</span>}</td>
                    <td className="py-1 text-right">{l.qty}</td>
                    <td className="py-1 text-right">{fmtMoney(l.rate, r.currency)}</td>
                    <td className="py-1 text-right">{l.taxRate || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
